/**
 * Portal Detection - Detect portal slug from hostname
 *
 * Supports subdomain-based routing:
 * - shop.retoure.shop  -> slug = "shop"  (production)
 * - shop.goe-returns.de -> slug = "shop" (staging)
 * - shop.goreturn.de   -> slug = "shop"  (legacy)
 * - www.retoure.shop   -> null (main app)
 * - localhost           -> null (dev)
 *
 * Custom Domain support:
 * - retoure.meinshop.de -> custom domain (resolved via API)
 */

const RESERVED_SUBDOMAINS = ['www', 'app', 'api', 'admin', 'portal', 'portals'];

const BASE_DOMAIN = import.meta.env.VITE_PORTAL_BASE_DOMAIN || 'retoure.shop';
const PORTAL_DOMAINS = [`.${BASE_DOMAIN}`, '.go-returns.de', '.go-returns.com', '.goreturn.de'];

export function getPortalSlugFromHostname(): string | null {
  const hostname = window.location.hostname;

  for (const domain of PORTAL_DOMAINS) {
    if (hostname.endsWith(domain)) {
      const sub = hostname.replace(domain, '');
      if (sub && !RESERVED_SUBDOMAINS.includes(sub) && !sub.includes('.')) {
        return sub;
      }
      return null;
    }
  }

  return null;
}

export function isPortalSubdomain(): boolean {
  return getPortalSlugFromHostname() !== null;
}

/**
 * Detect if the current hostname is a custom domain (not a known base domain).
 * Custom domains are resolved via the /api/portal/resolve endpoint.
 */
export function isCustomDomain(): boolean {
  const hostname = window.location.hostname;

  // Localhost / IP = not a custom domain
  if (
    hostname === 'localhost' ||
    hostname.startsWith('127.') ||
    hostname.startsWith('192.168.') ||
    hostname.includes('0.0.0.0')
  ) {
    return false;
  }

  // Known base domains = not a custom domain
  const knownRoots = PORTAL_DOMAINS.map((d) => d.replace(/^\./, ''));
  if (knownRoots.includes(hostname)) {
    return false;
  }

  // Known portal subdomain = not a custom domain
  for (const domain of PORTAL_DOMAINS) {
    if (hostname.endsWith(domain)) {
      return false;
    }
  }

  // Everything else = custom domain
  return true;
}

/**
 * Get the custom domain hostname (for API resolution).
 */
export function getCustomDomainHostname(): string | null {
  return isCustomDomain() ? window.location.hostname : null;
}
